import React, { forwardRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from './Field';
import { EndSelect } from './Field.styles';
import { currencyDecimals, currencySymbol } from '../../onboardingV2.locale';

const formatter = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });

/** 49900 → '49.900' (es-CO, no decimals). */
export const formatThousands = (value) =>
    value === '' || value === null || value === undefined ? '' : formatter.format(value);

/** '$ 49.900' → 49900; '' when there are no digits. Max 9 digits are kept for validation. */
export const parseDigits = (text) => {
    const digits = String(text || '')
        .replace(/\D/g, '')
        .slice(0, 10);
    return digits ? Number(digits) : '';
};

/**
 * Typed text → { value, text } for a currency with `decimals` fraction digits. While typing the
 * field shows only what the merchant typed (no thousands separators, those appear on blur), so
 * "," and "." both work for the cents: the last one is the decimal separator when at most
 * `decimals` digits follow it ('24,90' / '24.90' → 24.9, '1,000.50' → 1000.5), otherwise it groups
 * thousands ('3.464' → 3464). Currencies without cents (COP, CLP…) ignore separators ('50.000' → 50000).
 */
export const parseMoney = (raw, decimals) => {
    const text = String(raw || '').replace(/[^\d.,]/g, '');
    let sepIndex = -1;
    if (decimals > 0) {
        const last = Math.max(text.lastIndexOf(','), text.lastIndexOf('.'));
        if (last !== -1 && text.slice(last + 1).replace(/\D/g, '').length <= decimals) sepIndex = last;
    }
    const intDigits = (sepIndex === -1 ? text : text.slice(0, sepIndex)).replace(/\D/g, '').slice(0, 10);
    const fraction = sepIndex === -1 ? '' : text.slice(sepIndex + 1).replace(/\D/g, '');
    if (!intDigits && !fraction) return { value: '', text };
    return { value: Number(`${intDigits || 0}${fraction ? `.${fraction}` : ''}`), text };
};

const formatValue = (value, decimals, locale) =>
    value === '' || value === null || value === undefined
        ? ''
        : new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(value);

/** The value as the merchant edits it: no thousands separators, the locale's decimal separator. */
const editableValue = (value, decimals, locale) =>
    value === '' || value === null || value === undefined
        ? ''
        : new Intl.NumberFormat(locale, { useGrouping: false, maximumFractionDigits: decimals }).format(value);

/**
 * Price input formatted while typing (`$ 49.900 COP`). Stores a clean number (decimals only for
 * currencies that use them). With `currencyOptions` + `onCurrencyChange` the currency is a
 * selector. Meant for a react-hook-form `Controller`.
 */
const MoneyField = forwardRef(
    ({ value, onChange, onBlur, currency = 'COP', locale = 'es-CO', currencyOptions, onCurrencyChange, id, ...rest }, ref) => {
        const { t } = useTranslation();
        const decimals = useMemo(() => currencyDecimals(currency), [currency]);
        // While focused, the text as entered (e.g. '19,' before the cents); grouped again on blur.
        const [typing, setTyping] = useState(null);
        const selectable = Array.isArray(currencyOptions) && currencyOptions.length > 0 && onCurrencyChange;

        return (
            <TextField
                ref={ref}
                id={id}
                type="text"
                inputMode={decimals > 0 ? 'decimal' : 'numeric'}
                autoComplete="off"
                prefix={currencySymbol(currency, locale)}
                suffix={selectable ? null : currency}
                endAdornment={
                    selectable ? (
                        <EndSelect>
                            <span aria-hidden="true">{currency}</span>
                            <i className="ri-arrow-down-s-line" aria-hidden="true" />
                            <select
                                id={`${id}-currency`}
                                value={currency}
                                onChange={(event) => onCurrencyChange(event.target.value)}
                                aria-label={t('SignUpV2.producto.currencyLabel')}
                            >
                                {currencyOptions.map((option) => (
                                    <option key={option.code} value={option.code}>
                                        {`${option.code} · ${option.name}`}
                                    </option>
                                ))}
                            </select>
                        </EndSelect>
                    ) : null
                }
                value={typing !== null ? typing : formatValue(value, decimals, locale)}
                onFocus={() => setTyping(editableValue(value, decimals, locale))}
                onChange={(event) => {
                    const next = parseMoney(event.target.value, decimals);
                    setTyping(next.text);
                    onChange(next.value);
                }}
                onBlur={(event) => {
                    setTyping(null);
                    if (onBlur) onBlur(event);
                }}
                {...rest}
            />
        );
    }
);

export default MoneyField;
